import React, { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Select from 'react-select';
import { State } from 'country-state-city';
import countries from 'i18n-iso-countries';
import frCountries from 'i18n-iso-countries/langs/fr.json';
import enCountries from 'i18n-iso-countries/langs/en.json';
import paymentApiService from '../../services/paymentApi';
import { useTranslation } from 'react-i18next';

countries.registerLocale(frCountries);
countries.registerLocale(enCountries);

const flagEmoji = (iso2) =>
  iso2.toUpperCase().replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

const selectStyles = (hasError) => ({
  control: (base) => ({
    ...base,
    minHeight: 48,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: hasError ? '#dc3545' : '#ddd',
    boxShadow: 'none',
    fontSize: 16,
    '&:hover': { borderColor: hasError ? '#dc3545' : '#ddd' },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 20000 }),
  menu: (base) => ({ ...base, zIndex: 20000 }),
});

const regionsForCountry = (iso2, lang) => {
  const list = State.getStatesOfCountry(iso2) || [];
  const seen = new Set();
  const names = [];
  for (const region of list) {
    const name = region?.name?.trim().replace(/\s+region$/i, '');
    if (!name || seen.has(name)) continue;
    seen.add(name);
    names.push(name);
  }
  names.sort((a, b) => a.localeCompare(b, lang));
  return names.map((name) => ({ value: name, label: name }));
};

const PaymentPage = ({ onClose, pricingData }) => {
  const { t, i18n } = useTranslation();
  const cyberLocale = i18n.language === 'fr' ? 'fr-fr' : 'en-us';
  const countryLang = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'fr';
  const countryOptions = useMemo(() => {
    const names = countries.getNames(countryLang, { select: 'official' });
    return Object.entries(names)
      .map(([code, name]) => ({
        value: code,
        label: `${flagEmoji(code)}  ${name}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, countryLang));
  }, [countryLang]);
  const [cityQuery, setCityQuery] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'SN',
    referenceFirstName: '',
    referenceLastName: '',
    referencePhone: ''
  });

  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const cityOptions = useMemo(
    () => (formData.country ? regionsForCountry(formData.country, countryLang) : []),
    [formData.country, countryLang]
  );
  const visibleCityOptions = useMemo(() => {
    const query = cityQuery.trim().toLocaleLowerCase();
    const source = query
      ? cityOptions.filter((option) => option.label.toLocaleLowerCase().includes(query))
      : cityOptions;
    return source.slice(0, 80);
  }, [cityOptions, cityQuery]);
  const includeMddFields = (process.env.REACT_APP_INCLUDE_MDD_FIELDS || 'false').toLowerCase() === 'true';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('payment.form.validation.firstName');
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t('payment.form.validation.lastName');
    }

    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('payment.form.validation.email');
    }

    if (!formData.phone.trim() || formData.phone.replace(/\D/g, '').length < 8) {
      newErrors.phone = t('payment.form.validation.phone');
    }

    if (!formData.address.trim()) {
      newErrors.address = t('payment.form.validation.address');
    }

    if (!formData.city.trim()) {
      newErrors.city = t('payment.form.validation.city');
    }

    if (!formData.postalCode.trim()) {
      newErrors.postalCode = t('payment.form.validation.postalCode');
    }

    if (!formData.referenceFirstName.trim()) {
      newErrors.referenceFirstName = t('payment.form.validation.referenceFirstName');
    }

    if (!formData.referenceLastName.trim()) {
      newErrors.referenceLastName = t('payment.form.validation.referenceLastName');
    }

    if (!formData.referencePhone.trim() || formData.referencePhone.replace(/\D/g, '').length < 8) {
      newErrors.referencePhone = t('payment.form.validation.referencePhone');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);

    try {
      // Valider les données avec le service de paiement
      const validation = paymentApiService.validatePaymentData(formData);
      
      if (!validation.isValid) {
        setErrors(validation.errors);
        setIsProcessing(false);
        return;
      }

      // Soumettre le paiement à CyberSource
      console.log('🔄 Soumission du paiement...', formData);
      const result = await paymentApiService.submitPayment({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone.startsWith('+') ? formData.phone : `+${formData.phone}`,
        address: formData.address,
        city: formData.city,
        postalCode: formData.postalCode,
        country: formData.country,
        referenceFirstName: formData.referenceFirstName,
        referenceLastName: formData.referenceLastName,
        referencePhone: formData.referencePhone.startsWith('+')
          ? formData.referencePhone
          : `+${formData.referencePhone}`,
      }, pricingData, cyberLocale);
      
      if (result.success) {
        // La redirection vers CyberSource se fait automatiquement
        console.log('✅ Redirection vers CyberSource...', result.params);
        // Ne pas remettre isProcessing à false car la redirection va se faire
      } else {
        console.error('❌ Erreur lors de la soumission:', result.message);
        alert(`${t('payment.form.errors.submission')}: ${result.message}`);
        setIsProcessing(false);
      }
      
    } catch (error) {
      console.error('Erreur lors du traitement du paiement:', error);
      alert(t('payment.form.errors.processing'));
      setIsProcessing(false);
    }
  };

  return (
    <>
      <form id="local_reference_fields" hidden onSubmit={(e) => e.preventDefault()} />
      <div 
        className="payment-overlay" 
        onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '0'
      }}
    >
      <div 
        className="payment-content" 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          padding: '0',
          overflow: 'auto',
          position: 'relative',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* En-tête */}
        <div style={{
          background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
          padding: '30px 20px',
          textAlign: 'center',
          color: 'white',
          borderRadius: '20px 20px 0 0',
          minHeight: 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 15px',
            fontSize: '24px',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(255, 255, 255, 0.3)'
          }}>
            <Icon icon="material-symbols:credit-card" />
          </div>
          <h2 className="payment-title" style={{ 
            margin: 0, 
            fontWeight: 'bold',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            lineHeight: '1.2'
          }}>
            {t('subscription.payment.secureTitle')}
          </h2>
          <p style={{ 
            margin: '10px 0 0', 
            opacity: 0.9,
            fontSize: '16px',
            fontWeight: '500'
          }}>
            {pricingData?.title} - {pricingData?.price}
          </p>
        </div>

        {/* Bouton de fermeture */}
        <button 
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            background: 'rgba(255, 255, 255, 0.9)',
            border: '2px solid rgba(40, 167, 69, 0.3)',
            fontSize: '20px',
            cursor: 'pointer',
            color: '#28a745',
            padding: '8px',
            borderRadius: '50%',
            width: '35px',
            height: '35px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            zIndex: 10000,
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(10px)'
          }}
          onMouseOver={(e) => {
            e.target.style.background = 'rgba(40, 167, 69, 0.9)';
            e.target.style.color = 'white';
            e.target.style.transform = 'rotate(90deg) scale(1.1)';
            e.target.style.boxShadow = '0 6px 25px rgba(40, 167, 69, 0.4)';
          }}
          onMouseOut={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.9)';
            e.target.style.color = '#28a745';
            e.target.style.transform = 'rotate(0deg) scale(1)';
            e.target.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.2)';
          }}
        >
          <Icon icon="material-symbols:close" />
        </button>

        {/* Formulaire de paiement */}
        <div className="payment-form-wrap">
          <form onSubmit={handleSubmit} id="payment_form">
            {/* Champs cachés CyberSource - seront remplis par le service */}
            {/* Champs signés */}
            <input type="hidden" name="profile_id" />
            <input type="hidden" name="access_key" />
            <input type="hidden" name="transaction_uuid" />
            <input type="hidden" name="signed_date_time" />
            <input type="hidden" name="signed_field_names" />
            <input type="hidden" name="unsigned_field_names" />
            <input type="hidden" name="transaction_type" value="sale" />
            <input type="hidden" name="reference_number" />
            <input type="hidden" name="auth_trans_ref_no" />
            <input type="hidden" name="amount" />
            <input type="hidden" name="currency" value="XOF" />
            <input type="hidden" name="locale" value={cyberLocale} />
            <input type="hidden" name="merchant_descriptor" />
            <input type="hidden" name="override_custom_cancel_page" />
            <input type="hidden" name="override_custom_receipt_page" />
            
            {/* Champs non signés */}
            <input type="hidden" name="signature" />
            <input type="hidden" name="bill_to_forename" />
            <input type="hidden" name="bill_to_surname" />
            <input type="hidden" name="bill_to_email" />
            <input type="hidden" name="bill_to_phone" />
            <input type="hidden" name="bill_to_address_line1" />
            <input type="hidden" name="bill_to_address_line2" />
            <input type="hidden" name="bill_to_address_city" />
            <input type="hidden" name="bill_to_address_state" />
            <input type="hidden" name="bill_to_address_country" />
            <input type="hidden" name="bill_to_address_postal_code" />
            <input type="hidden" name="customer_ip_address" />
            <input type="hidden" name="merchant_defined_data1" />
            <input type="hidden" name="merchant_defined_data2" />
            <input type="hidden" name="merchant_defined_data3" />
            <input type="hidden" name="merchant_defined_data4" />
            {/* Informations de facturation */}
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ 
                color: '#333', 
                marginBottom: '20px', 
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <Icon icon="material-symbols:person" />
                {t('subscription.payment.billingInfo')}
              </h3>
              
              <div className="payment-grid-2">
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#555' }}>
                    {t('payment.form.fields.firstName')} *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder={t('payment.form.placeholders.firstName')}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `2px solid ${errors.firstName ? '#dc3545' : '#ddd'}`,
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'border-color 0.3s ease'
                    }}
                  />
                  {errors.firstName && (
                    <span style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px', display: 'block' }}>
                      {errors.firstName}
                    </span>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#555' }}>
                    {t('payment.form.fields.lastName')} *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder={t('payment.form.placeholders.lastName')}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `2px solid ${errors.lastName ? '#dc3545' : '#ddd'}`,
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'border-color 0.3s ease'
                    }}
                  />
                  {errors.lastName && (
                    <span style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px', display: 'block' }}>
                      {errors.lastName}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Informations de contact */}
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ 
                color: '#333', 
                marginBottom: '20px', 
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <Icon icon="material-symbols:contact-mail" />
                {t('subscription.payment.contactInfo')}
              </h3>
              
              <div className="payment-grid-2">
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#555' }}>
                    {t('payment.form.fields.email')} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('payment.form.placeholders.email')}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `2px solid ${errors.email ? '#dc3545' : '#ddd'}`,
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'border-color 0.3s ease'
                    }}
                  />
                  {errors.email && (
                    <span style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px', display: 'block' }}>
                      {errors.email}
                    </span>
                  )}
                </div>

                <div className={errors.phone ? 'payment-phone payment-phone-error' : 'payment-phone'}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#555' }}>
                    {t('payment.form.fields.phone')} *
                  </label>
                  <PhoneInput
                    country="sn"
                    value={formData.phone}
                    onChange={(value) => setFormData((prev) => ({ ...prev, phone: value }))}
                    enableSearch
                    searchPlaceholder={countryLang === 'en' ? 'Search' : 'Rechercher'}
                    preferredCountries={['sn', 'fr', 'ci', 'ml', 'bf', 'gn', 'gm', 'tg', 'bj']}
                    containerStyle={{ width: '100%' }}
                    inputStyle={{
                      width: '100%',
                      height: '48px',
                      border: `2px solid ${errors.phone ? '#dc3545' : '#ddd'}`,
                      borderRadius: '8px',
                      fontSize: '16px',
                    }}
                    buttonStyle={{
                      border: `2px solid ${errors.phone ? '#dc3545' : '#ddd'}`,
                      borderRadius: '8px 0 0 8px',
                      background: '#fff',
                    }}
                    dropdownStyle={{ zIndex: 20000 }}
                  />
                  {errors.phone && (
                    <span style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px', display: 'block' }}>
                      {errors.phone}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#555' }}>
                  {t('payment.form.fields.address')} *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder={t('payment.form.placeholders.address')}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `2px solid ${errors.address ? '#dc3545' : '#ddd'}`,
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'border-color 0.3s ease'
                  }}
                />
                {errors.address && (
                  <span style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px', display: 'block' }}>
                    {errors.address}
                  </span>
                )}
              </div>

              <div className="payment-grid-location">
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#555' }}>
                    {t('payment.form.fields.country')}
                  </label>
                  <Select
                    inputId="payment-country"
                    options={countryOptions}
                    value={countryOptions.find((option) => option.value === formData.country) || null}
                    onChange={(option) => {
                      setCityQuery('');
                      setFormData((prev) => ({ ...prev, country: option?.value || '', city: '' }));
                    }}
                    isSearchable
                    placeholder={countryLang === 'en' ? 'Search a country' : 'Rechercher un pays'}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                    styles={selectStyles(false)}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#555' }}>
                    {t('payment.form.fields.city')} *
                  </label>
                  {cityOptions.length > 0 ? (
                    <Select
                      inputId="payment-city"
                      options={visibleCityOptions}
                      value={formData.city ? { value: formData.city, label: formData.city } : null}
                      onChange={(option) => setFormData((prev) => ({ ...prev, city: option?.value || '' }))}
                      onInputChange={(value, meta) => {
                        if (meta.action === 'input-change') setCityQuery(value);
                        if (meta.action === 'menu-close') setCityQuery('');
                      }}
                      filterOption={() => true}
                      isSearchable
                      isClearable
                      placeholder={countryLang === 'en' ? 'Search a region' : 'Rechercher une région'}
                      noOptionsMessage={() => (countryLang === 'en' ? 'No region' : 'Aucune région')}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition="fixed"
                      styles={selectStyles(Boolean(errors.city))}
                    />
                  ) : (
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder={t('payment.form.placeholders.city')}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: `2px solid ${errors.city ? '#dc3545' : '#ddd'}`,
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'border-color 0.3s ease'
                      }}
                    />
                  )}
                  {errors.city && (
                    <span style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px', display: 'block' }}>
                      {errors.city}
                    </span>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#555' }}>
                    {t('payment.form.fields.postalCode')} *
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    placeholder={t('payment.form.placeholders.postalCode')}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `2px solid ${errors.postalCode ? '#dc3545' : '#ddd'}`,
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'border-color 0.3s ease'
                    }}
                  />
                  {errors.postalCode && (
                    <span style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px', display: 'block' }}>
                      {errors.postalCode}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h3 style={{
                color: '#333',
                marginBottom: '20px',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <Icon icon="material-symbols:person-pin" />
                {t('payment.form.reference.title')}
              </h3>
              <div className="payment-grid-2">
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#555' }}>
                    {t('payment.form.reference.firstName')} *
                  </label>
                  <input
                    type="text"
                    form="local_reference_fields"
                    value={formData.referenceFirstName}
                    onChange={(e) => setFormData((prev) => ({ ...prev, referenceFirstName: e.target.value }))}
                    placeholder={t('payment.form.placeholders.referenceFirstName')}
                    autoComplete="off"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `2px solid ${errors.referenceFirstName ? '#dc3545' : '#ddd'}`,
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'border-color 0.3s ease'
                    }}
                  />
                  {errors.referenceFirstName && (
                    <span style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px', display: 'block' }}>
                      {errors.referenceFirstName}
                    </span>
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#555' }}>
                    {t('payment.form.reference.lastName')} *
                  </label>
                  <input
                    type="text"
                    form="local_reference_fields"
                    value={formData.referenceLastName}
                    onChange={(e) => setFormData((prev) => ({ ...prev, referenceLastName: e.target.value }))}
                    placeholder={t('payment.form.placeholders.referenceLastName')}
                    autoComplete="off"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `2px solid ${errors.referenceLastName ? '#dc3545' : '#ddd'}`,
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'border-color 0.3s ease'
                    }}
                  />
                  {errors.referenceLastName && (
                    <span style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px', display: 'block' }}>
                      {errors.referenceLastName}
                    </span>
                  )}
                </div>
                <div className={errors.referencePhone ? 'payment-phone payment-phone-error' : 'payment-phone'}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#555' }}>
                    {t('payment.form.reference.phone')} *
                  </label>
                  <PhoneInput
                    country="sn"
                    value={formData.referencePhone}
                    onChange={(value) => setFormData((prev) => ({ ...prev, referencePhone: value }))}
                    enableSearch
                    searchPlaceholder={countryLang === 'en' ? 'Search' : 'Rechercher'}
                    preferredCountries={['sn', 'fr', 'ci', 'ml', 'bf', 'gn', 'gm', 'tg', 'bj']}
                    inputProps={{ form: 'local_reference_fields', autoComplete: 'off' }}
                    containerStyle={{ width: '100%' }}
                    inputStyle={{
                      width: '100%',
                      height: '48px',
                      border: `2px solid ${errors.referencePhone ? '#dc3545' : '#ddd'}`,
                      borderRadius: '8px',
                      fontSize: '16px',
                    }}
                    buttonStyle={{
                      border: `2px solid ${errors.referencePhone ? '#dc3545' : '#ddd'}`,
                      borderRadius: '8px 0 0 8px',
                      background: '#fff',
                    }}
                    dropdownStyle={{ zIndex: 20000 }}
                  />
                  {errors.referencePhone && (
                    <span style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px', display: 'block' }}>
                      {errors.referencePhone}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Information sur le processus de paiement */}
            <div style={{
              backgroundColor: '#e3f2fd',
              padding: '20px',
              borderRadius: '10px',
              marginBottom: '30px',
              border: '1px solid #bbdefb'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <Icon icon="material-symbols:info" style={{ color: '#1976d2', fontSize: '20px' }} />
                <span style={{ fontWeight: '500', color: '#333' }}>{t('subscription.payment.process.title')}</span>
              </div>
              <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                {t('subscription.payment.process.text')}
              </p>
            </div>
            {/* addresse transvie diaspora */}
            <div style={{ 
              marginBottom: '30px',
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <div style={{
                  backgroundColor: '#28a745',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Icon 
                    icon="ep:location" 
                    style={{ 
                      color: 'white', 
                      fontSize: '18px' 
                    }} 
                  />
                </div>
                <div style={{
                  flex: 1,
                  color: '#495057',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                    {t('footer.contact.addressLine1')}
                  </div>
                  <div style={{ color: '#6c757d' }}>
                    {t('footer.contact.addressLine2')}
                  </div>
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="payment-actions">
              <button
                type="button"
                onClick={onClose}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '25px',
                  padding: '12px 30px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minWidth: '120px'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
              >
                {t('subscription.payment.cta.cancel')}
              </button>

              <button
                type="submit"
                disabled={isProcessing}
                style={{
                  backgroundColor: isProcessing ? '#6c757d' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '25px',
                  padding: '12px 30px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  minWidth: '150px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => {
                  if (!isProcessing) {
                    e.target.style.backgroundColor = '#218838';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isProcessing) {
                    e.target.style.backgroundColor = '#28a745';
                  }
                }}
              >
                {isProcessing ? (
                  <>
                    <Icon icon="eos-icons:loading" style={{ animation: 'spin 1s linear infinite' }} />
                    {t('subscription.payment.cta.processing')}
                  </>
                ) : (
                  <>
                    <Icon icon="material-symbols:arrow-forward" />
                    {t('subscription.payment.cta.continue')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        </div>
      </div>

      {/* Animation CSS */}
      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .payment-overlay {
          animation: fadeIn 0.3s ease-out;
        }
        
        .payment-content {
          animation: slideIn 0.4s ease-out;
          width: min(1000px, 90vw);
          height: min(800px, 90vh);
          border-radius: 20px;
        }

        .payment-content,
        .payment-content * {
          box-sizing: border-box;
        }

        .payment-title {
          font-size: 28px;
        }

        .payment-form-wrap {
          padding: 30px 40px 40px;
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          max-width: 800px;
          margin: 0 auto;
          width: 100%;
        }

        .payment-grid-2,
        .payment-grid-location {
          display: grid;
          gap: 20px;
        }

        .payment-grid-2 {
          grid-template-columns: 1fr 1fr;
          margin-bottom: 20px;
        }

        .payment-grid-location {
          grid-template-columns: minmax(0, 1.15fr) minmax(0, 2.1fr) 120px;
          gap: 16px;
        }

        .payment-grid-2 > *,
        .payment-grid-location > * {
          min-width: 0;
        }

        .payment-actions {
          display: flex;
          gap: 20px;
          justify-content: center;
          margin-top: 30px;
          flex-wrap: wrap;
        }

        .payment-content .react-tel-input,
        .payment-content .react-tel-input .form-control {
          width: 100% !important;
          max-width: 100%;
        }

        @media (max-width: 720px) {
          .payment-overlay {
            padding: 0;
          }

          .payment-content {
            width: 100vw;
            height: 100dvh;
            max-height: none;
            border-radius: 0;
          }

          .payment-title {
            font-size: 22px;
          }

          .payment-form-wrap {
            padding: 20px 16px 28px;
            justify-content: flex-start;
          }

          .payment-grid-2,
          .payment-grid-location {
            grid-template-columns: 1fr;
          }

          .payment-actions {
            flex-direction: column;
          }

          .payment-actions button {
            width: 100%;
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-50px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </>
  );
};

export default PaymentPage;
